// Phase 5 "SPORE SAYS": per-level sequences + show timing. Symbols: TAP (●), STROKE (▬), OFF (⏸ hands off).
export const SAYS_LEVELS = [
  { feeling: 'curiosity', sequence: ['TAP', 'TAP'] },
  { feeling: 'pleasure', sequence: ['STROKE', 'STROKE'] },
  { feeling: 'pain', sequence: ['TAP', 'TAP', 'TAP'] },
  { feeling: 'fear', sequence: ['MASSAGE', 'TAP'], intro: [{ say: 'fear.tease' }, { sing: 'MASSAGE' }] },
  { feeling: 'love', sequence: ['STROKE', 'TAP', 'STROKE'] },
  { feeling: 'jealousy', sequence: ['TAP', 'TAP', 'STROKE', 'STROKE'] },
  { feeling: 'hate', sequence: ['TAP', 'TAP', 'STROKE', 'TAP'] },
  { feeling: 'embarrassment', sequence: ['MASSAGE', 'TAP', 'TAP', 'TAP'] },
  { feeling: 'boredom', sequence: ['STROKE', 'OFF3', 'STROKE'] },
  { feeling: 'loneliness', sequence: ['TAP', 'OFF5'] },
  { feeling: 'desire', sequence: ['MASSAGE', 'MASSAGE', 'STROKE'] },
  { feeling: 'ecstasy', sequence: ['MASSAGE', 'STROKE', 'TAP', 'TAP', 'STROKE', 'TAP', 'STROKE'] },
];
// Henri teaches the sounds in the rules: line, SPORE's sound, line, sound, line
export const RULES_TEACH = [{ say: 'rules.tap' }, { sing: 'TAP' }, { say: 'rules.stroke' }, { sing: 'STROKE' }, { say: 'rules.repeat' }];

// hive root-word glosses (thesporeproject.com's 12 roots; the site dictionary's recipe where it defines the feeling)
export const ROOT_GLOSS = {
  curiosity: { roots: ['DEEP', 'DARK', 'SPORE'], gloss: 'a spore in the deep dark' },
  pleasure: { roots: ['WARM', 'SOFT', 'WARM'], gloss: 'warm, soft, and warm again' },
  pain: { roots: ['FAST', 'ROT', 'SELF'], gloss: 'I am bruising, quickly' },
  fear: { roots: ['DARK', 'OTHER', 'FAST'], gloss: 'something in the dark, coming quickly' },
  love: { roots: ['WARM', 'OTHER'], gloss: 'the other one is warm' },
  jealousy: { roots: ['OTHER', 'LIGHT', 'ROT'], gloss: "the neighbour's light is rotting my cap" },
  hate: { roots: ['ROT', 'OTHER', 'ROT'], gloss: 'rot to the other one, and more rot' },
  embarrassment: { roots: ['WARM', 'SELF'], gloss: 'I have become warm' },
  boredom: { roots: ['SOFT', 'SOFT', 'SOFT'], gloss: 'soft. soft. soft.' },
  loneliness: { roots: ['DARK', 'SELF'], gloss: 'just me, in the dark' },
  desire: { roots: ['WARM', 'FAST', 'OTHER'], gloss: 'warm, quickly, towards another' },
  ecstasy: { roots: ['SPORE', 'SPORE', 'LIGHT'], gloss: 'all my spores, alight at once' },
};
export const glossLine = (f) => (ROOT_GLOSS[f] ? `${ROOT_GLOSS[f].roots.join(' + ')} · '${ROOT_GLOSS[f].gloss}'` : '');

export const SYMBOLS = {
  TAP: { glyph: '●', label: 'TAP' },
  STROKE: { glyph: '▬', label: 'STROKE' },
  MASSAGE: { glyph: '≋', label: 'MASSAGE' },
  OFF3: { glyph: '⏸', label: 'HANDS OFF 3 s' },
  OFF5: { glyph: '⏸', label: 'HANDS OFF 5 s' },
};
// OFF3 / OFF5 = hands off for 3 / 5 seconds
export const isOff = (sym) => typeof sym === 'string' && sym.startsWith('OFF');
export const offMsOf = (sym) => Number(sym.slice(3)) * 1000;

export const SAYS_CONFIG = {
  demo: { preMs: 500, gapMs: 400, endMs: 400, TAP: 650, STROKE: 2000, MASSAGE: 5400, OFF3: 1800, OFF5: 2400 },
  timeoutMs: 8000, // mid-sequence: no input for 8 s -> sigh + reset
  wrongMs: 1600, // grumble, then SPORE re-sings
  wrongLineAtMs: 900, // Henri's cheeky "let someone else 'ave a go" (once per experiment) after the grumble
  wrongLines: 5,
  volunteerEvery: 2, // after experiments 2, 4, 6, 8, 10
  volunteerLines: 5,
  volunteerMinMs: 800,
  teachGapMs: 500,
  offForgiveAfter: 3, // a hands-off step broken this often while a word is faulty passes on its timer
  lenientAfter: [2, 4], // failed attempts before tolerance widens / any touch counts (host HUD only; Henri encourages)
  encourageLines: 3,
  nudgeAfterS: 30,
  hostHintAfterS: 60,
  openingMinMs: 1500,
  ritualMaxMs: 30000,
  rulesMinMs: 1000,
  instructMinMs: 600,
  unlockMs: 3500,
  interpretMinMs: 1000,
  interpretEarlyMs: 1500,
  analysisMs: 2500,
  maxTalkMs: 15000,
  finaleMaxMs: 60000,
  memoryMinMs: 3500, // (legacy) superseded by storyPacing
  // reveal → memory pacing: every step waits for the previous audio to END (no fixed-timer overlaps)
  storyPacing: {
    beatMs: 1500, // stamps settle, the mycelium dims: scene change
    cueMs: 900, // soft "memory" sound as the screen shifts into memory mode
    fadeInMs: 800, // the card fades in...
    readHoldMs: 2000, // ...and holds silently so the room can read it
    postVoiceHoldMs: 2500, // the card stays after Henri's line ends
    minCardMs: 7000, // card on screen >= max(7 s, words × msPerWord + voice + postVoiceHoldMs)
    msPerWord: 350,
    fadeOutMs: 800,
    afterMs: 1000, // beat before the next experiment's instruction
    lineGapMs: 1000, // breathing gap between consecutive Henri lines (prologue, finale, everywhere)
  },
  broochMinMs: 3000,
  storyMaxMs: 60000, // opening + prologue, finale request, reveal + farewell: never cut Henri off
  mmmmMs: 2600,
  closingMinMs: 2500,
};

// Phase 8 story: "The Disappearance of Margaux Champignon". One memory fragment per decoded experiment.
export const STORY_ACTS = [
  { n: 1, roman: 'I', title: 'THE MISSION' },
  { n: 2, roman: 'II', title: 'THE DARKNESS' },
  { n: 3, roman: 'III', title: 'THE OUTBREAK' },
];
export const actOf = (levelIdx) => STORY_ACTS[Math.min(2, Math.floor(levelIdx / 4))];
export const MEMORY_FRAGMENTS = {
  curiosity: 'Woman in field uniform. Curious, unafraid. A small red bird pinned to her coat.',
  pleasure: 'She loved the work. Spoke to the hive softly, with gentle hands.',
  pain: 'Something in the dark hurt her. The specimen recorded the wound.',
  fear: "Soldiers sealing the doors. Whispers in the corridor: 'the Darkness'.",
  love: 'She spoke of a husband she missed. Every night, the same name.',
  jealousy: 'The Cult of the Darkness envied her bond with the hive. She was among them, undercover.',
  hate: 'Mission compromised. Order received: destroy the hive. She refused.',
  embarrassment: "Cult spore ceremonies. Very intimate. Location confirmed: this building, Gray's Inn Road.",
  boredom: 'Quarantine. Day 14. Day 31. Day 58. The doors stay sealed.',
  loneliness: 'One by one, the others fell silent.',
  desire: 'She longed to get home. To him.',
  ecstasy: 'Outbreak of the Darkness. Spores bloomed through the building. 4th floor: all taken.',
};

// calm-down tendril game (touchscreen): ~20 soothes fill the meter, auto-completes at 60 s
export const CALM_CONFIG = {
  maxPerShow: 2,
  soothesToCalm: 20,
  maxMs: 60000,
  encourageAt: 50, // % calm: Henri's one mid-game line
  lifeMs: [1500, 2500],
  spawnMs: [950, 520], // spawn interval, start → end (speeds up as she calms)
  maxLive: 6,
  safePhases: ['intro', 'instruct', 'demo', 'try', 'wrong'],
  blockedPhases: ['title', 'opening', 'ritual', 'rules', 'finale', 'search', 'brooch', 'mmmm', 'closing', 'end', 'calm'],
};
